import { type App, type Ref, ref, watch } from "vue";

import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ICustomerArticle, ICustomerOptionGroup } from "@/data/model/Customer";
import { CurrentLocationService } from "@/data/services/CurrentLocationService";
import { Format } from "@/data/util/Format";

import { clientLanguage } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { toMoneyWithSign } from "@/shared/utils/format";
import { createVueComponent } from "@/shared/utils/vue";

import CurrencyInput from "@/components/elements/CurrencyInput.vue";

import { TastierClient } from "@/app/TastierClient";
import { OptionGroup } from "@/app/elements/OptionGroup";
import { PayButton } from "@/app/elements/PayButton";
import { QuantityInput } from "@/app/elements/QuantityInput";
import { trans } from "@/app/localization/Trans";
import { BasePopover } from "@/app/popover/BasePopover";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";

export class AddToCardPopover extends BasePopover {
    private $articleInfo: JQuery;
    private $articleName: JQuery;
    private $price: JQuery;
    private $scrollable: JQuery;
    private $shortDescription: JQuery;
    private $longDescription: JQuery;
    private $optionGroups: JQuery;

    private $bottom: JQuery;
    private addBtn: PayButton;

    private currentArticle?: ICustomerArticle;
    private quantityInput: QuantityInput;
    private optionGroups: Array<OptionGroup>;

    private $openPriceRow: JQuery;
    private openPriceValue: Ref<number | null>;
    private openPriceInput: App<Element> | null | undefined;

    constructor() {
        super(undefined, false);

        this.$element.addClass("addtocard");

        this.$articleInfo = $('<div class="articleinfo" />').appendTo(this.$body);
        this.$articleName = $('<div class="articlename" />').appendTo(this.$articleInfo);
        this.$price = $('<div class="price" />').appendTo(this.$articleInfo);
        this.$scrollable = $('<div class="scrollable" />').appendTo(this.$body);
        this.$shortDescription = $('<div class="shortdescription" />').appendTo(this.$scrollable);
        this.$longDescription = $('<div class="longdescription" />').appendTo(this.$scrollable);

        this.$openPriceRow = $(
            `<div class="openpricerow">
                <label for="open-price-input">${trans.addToCartPopover.price}</label>
                <div class="open-price-input-container"></div>
            </div>`
        ).appendTo(this.$scrollable);

        this.openPriceValue = ref(null as number | null);
        watch(this.openPriceValue, () => {
            this.validateBtnAndUpdatePrice();
        });

        this.optionGroups = new Array<OptionGroup>();
        this.$optionGroups = $('<div class="optiongroups" />').appendTo(this.$scrollable);

        this.$bottom = $('<div class="bottom" />').appendTo(this.$body);
        this.quantityInput = new QuantityInput(1).appendTo(this.$bottom);
        this.quantityInput.onChange = () => this.validateBtnAndUpdatePrice();

        this.addBtn = new PayButton(trans.general.add).appendTo(this.$bottom);
        this.addBtn.onClick = async () => {
            this.addBtn.setDisabled(trans.addToCartPopover.adding);

            const options = [] as any[];
            for (const optionGroup of this.optionGroups) {
                options.push(...optionGroup.getValue());
            }

            let openPrice = undefined as number | undefined;
            if (this.currentArticle!.isOpenPriceEnabled) {
                openPrice = this.getOpenPriceInputValue();
            }

            try {
                await TastierClient.context.addArticleToCart(
                    this.currentArticle!.articleId,
                    this.currentArticle!.offerId,
                    this.quantityInput.getValue(),
                    options,
                    openPrice,
                    this.currentArticle!.categoryId,
                );
                this.hide();
                this.addBtn.setDisabled();
            } catch (ex) {
                logError(ex);
                Popup.show(trans.cart.failedToAdd);
                this.validateBtnAndUpdatePrice();
            }
        };
    }

    public forArticle(article: ICustomerArticle): AddToCardPopover {
        this.currentArticle = article;
        this.destroyOptionGroups();

        this.addBtn.setDisabled();
        this.$articleName.html(Format.htmlEscape(Format.translate(article.name, clientLanguage()))!);

        if (article.isOpenPriceEnabled) {
            this.$openPriceRow.show();
            this.$price.html("");
        } else {
            this.$openPriceRow.hide();
            const p = Helper.formatPrice(article);
            this.$price.html(p === "" ? "" : "CHF " + p);
        }

        const shortDesc = Format.translate(article.shortDescription, clientLanguage());
        if (shortDesc === undefined) this.$shortDescription.html("").hide();
        else this.$shortDescription.html(Format.htmlEscape(shortDesc)!).show();

        const longDesc = Format.translate(article.longDescription, clientLanguage());
        if (longDesc === undefined) this.$longDescription.html("").hide();
        else this.$longDescription.html(Format.htmlEscape(longDesc)!).show();

        const articleQuantityCurrentlyInCart = this.getArticleInCartQuantity(article);
        const maxArticleQuantityToAdd = article.maxQuantity! - articleQuantityCurrentlyInCart;
        this.quantityInput.setMin(article.minQuantity ?? 1);
        this.quantityInput.setMax(maxArticleQuantityToAdd);
        this.quantityInput.setValue(1);

        if (maxArticleQuantityToAdd == 1) {
            this.quantityInput.hide();
        } else {
            this.quantityInput.show();
        }

        if (this.currentArticle.hasOptions) {
            this.setFullScreen(true);
            this.loadOptionGroups(article);
        } else {
            this.setFullScreen(false);
            this.validateBtnAndUpdatePrice();
        }

        return this;
    }

    protected onShow() {
        if (this.openPriceInput == null) {
            const props = {
                id: "open-price-input",
                modelValue: this.openPriceValue,
            };
            const result = createVueComponent(
                CurrencyInput,
                props,
                document.querySelector(".open-price-input-container")!
            );
            this.openPriceInput = result.componentApp;
        }

        const height = this.$articleName.height();
        if (this.fullscreen) {
            this.$scrollable.removeAttr("style").css({
                top: height + 9,
            });
        } else {
            this.$scrollable.removeAttr("style");
        }

        this.updateBodyHeight(false);
    }

    protected onHide() {
        this.openPriceValue.value = null;
        this.$scrollable.removeAttr("style");
    }

    protected onDestroy() {
        this.destroyOptionGroups();
    }

    private destroyOptionGroups() {
        if (this.optionGroups.length === 0) return;

        for (const optionGroup of this.optionGroups) {
            optionGroup.destroy();
        }

        this.optionGroups = new Array<OptionGroup>();
    }

    private async loadOptionGroups(article: ICustomerArticle) {
        const offer = TastierClient.context.currentOffer;
        // todo: remove this check and ELSE code once we confirm that async loading is not needed
        if (offer?.offerOptionGroups != null) {
            try {
                let optionGroups: ICustomerOptionGroup[] = [];
                if (article.sortedOptionGroups != null) {
                    optionGroups = article.sortedOptionGroups.map((x) => {
                        const offerOptionGroup = offer.offerOptionGroups!.find((y) => y.optionGroupId === x);
                        if (offerOptionGroup != null) {
                            return offerOptionGroup;
                        }

                        throw Error("Offer option group not found!");
                    });
                }

                for (const optionGroup of optionGroups) {
                    const group = new OptionGroup(optionGroup, article.price !== 0).appendTo(this.$optionGroups);
                    group.onChange = () => this.validateBtnAndUpdatePrice();
                    this.optionGroups.push(group);
                }

                this.validateBtnAndUpdatePrice();
            } catch (ex) {
                logError(ex);
                Popup.show(trans.addToCartPopover.failedLoadingOptions);
                this.validateBtnAndUpdatePrice();
            }
            return;
        }

        this.$optionGroups.html(
            '<div class="loading"><div class="loader"></div><div class="lbl">' +
                trans.addToCartPopover.loadingOptions +
                "</div></div>"
        );
        const useCase = Helper.getCurrentOrderServiceUseCase();
        try {
            if (useCase == null) {
                throw Error("Use case is null!");
            }
            const offerId = TastierClient.context.currentOffer?.offerId;
            if (offerId == null) {
                throw Error("Offer id is null!");
            }
            const locationId = CurrentLocationService.getLocationId();
            if (locationId == null) {
                throw Error("Location id is null!");
            }

            const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
            const optionGroups = await customerDao.getOptionsOfArticle({
                article: article.articleId,
                offerId: offerId,
                useCaseId: useCase.id,
                locationId: locationId,
            });
            this.$optionGroups.empty();

            for (const optionGroup of optionGroups) {
                const group = new OptionGroup(optionGroup, article.price !== 0).appendTo(this.$optionGroups);
                group.onChange = () => this.validateBtnAndUpdatePrice();
                this.optionGroups.push(group);
            }

            this.validateBtnAndUpdatePrice();
        } catch (ex) {
            logError(ex);
            Popup.show(trans.addToCartPopover.failedLoadingOptions);
            this.validateBtnAndUpdatePrice();
        }
    }

    private getOpenPriceInputValue() {
        const value = this.openPriceValue.value;
        if (value == null || isNaN(value)) return 0;
        else return value;
    }

    private isCorrectOpenPriceEntered(): boolean {
        const value = this.openPriceValue.value;
        return !(value == null || isNaN(value));
    }

    private validateBtnAndUpdatePrice() {
        let isValid = true;
        let singlePrice = this.currentArticle!.isOpenPriceEnabled
            ? this.getOpenPriceInputValue()
            : Helper.sitePrice(this.currentArticle!);

        if (this.currentArticle!.isOpenPriceEnabled) {
            if (!this.isCorrectOpenPriceEntered()) {
                isValid = false;
            }
        }

        const quantity = this.quantityInput.getValue();

        for (const group of this.optionGroups) {
            if (!group.isValid()) isValid = false;

            singlePrice += group.calculatePrice();
        }

        const totalPrice = singlePrice * quantity;
        this.addBtn.setAmount(toMoneyWithSign(totalPrice));

        const articleQuantityCurrentlyInCart = this.getArticleInCartQuantity(this.currentArticle!);
        const maxArticleQuantityToAdd = this.currentArticle!.maxQuantity! - articleQuantityCurrentlyInCart;
        if (maxArticleQuantityToAdd == 0) isValid = false;

        if (isValid) this.addBtn.setEnabled();
        else this.addBtn.setDisabled();
    }

    private getArticleInCartQuantity(article: ICustomerArticle) {
        const currentOffer = TastierClient.context.currentOffer;
        if (currentOffer === undefined || currentOffer.cart === undefined) return 0;

        let totalArticleQuantity = 0;
        for (const item of currentOffer.cart.items) {
            if (item.articleId == article.articleId) {
                totalArticleQuantity += item.quantity;
            }
        }

        return totalArticleQuantity;
    }
}
